import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { get } from 'lodash';
import qs from 'query-string';

import { NotificationAPI } from 'apis/notification';
import { fetcher } from 'util/Api';

const EMPTY_NOTIFICATIONS = [];
const EMPTY_PAGES = [];

function useNotificationsQuery(params = {}, options = {}) {
  const { page = 1, status, type } = params;
  const url = qs.stringifyUrl({
    url: '/api/v1/notifications',
    query: { page, status, type },
  });

  const query = useQuery({
    queryKey: ['notifications', 'list', page, status, type],
    queryFn: () => fetcher(url),
    ...options,
  });

  const notifications = get(query.data, 'data', EMPTY_NOTIFICATIONS);
  const currentPage = get(query.data, 'current_page', 1);
  const perPage = get(query.data, 'per_page', 10);
  const total = get(query.data, 'total', 0);
  const hasData = query.data !== undefined;

  return {
    notifications,
    pagination: {
      current: currentPage,
      pageSize: perPage,
      total,
    },
    hasData,
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Page-by-page variant of `useNotificationsQuery` for surfaces that must reach
 * every unread row rather than just the first page.
 */
function useInfiniteNotificationsQuery(params = {}, options = {}) {
  const { status, type } = params;

  const query = useInfiniteQuery({
    queryKey: ['notifications', 'list', 'infinite', status, type],
    queryFn: ({ pageParam }) =>
      fetcher(
        qs.stringifyUrl({
          url: '/api/v1/notifications',
          query: { page: pageParam, status, type },
        })
      ),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const currentPage = get(lastPage, 'current_page', 1);
      const perPage = get(lastPage, 'per_page', 0);
      const total = get(lastPage, 'total', 0);

      return currentPage * perPage < total ? currentPage + 1 : undefined;
    },
    ...options,
  });

  const pages = get(query.data, 'pages', EMPTY_PAGES);
  const hasData = query.data !== undefined;

  return {
    pages,
    hasData,
    loading: query.isLoading,
    error: query.error,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  };
}

function useUnreadCountQuery(options = {}) {
  const query = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => fetcher('/api/v1/notifications/unread-count'),
    ...options,
  });

  // The endpoint answers `{status, data: {unread_count}}` — reading `count` off
  // the envelope always yielded undefined, so the badge silently fell back to
  // recounting page 1 and could never exceed the 15-row page size.
  const count = get(query.data, 'data.unread_count');
  const hasData = query.data !== undefined;

  return {
    count,
    hasData,
    loading: query.isLoading,
    refetch: query.refetch,
  };
}

function useNotificationSettingsQuery(options = {}) {
  const query = useQuery({
    queryKey: ['notifications', 'settings'],
    queryFn: NotificationAPI.getNotificationSettings,
    ...options,
  });

  return {
    ...query,
    settings: get(query.data, 'data', query.data || {}),
    loading: query.isLoading,
  };
}

export { useInfiniteNotificationsQuery, useNotificationsQuery, useNotificationSettingsQuery, useUnreadCountQuery };
